import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import Box from "@mui/material/Box";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VrpanoIcon from '@mui/icons-material/Vrpano';
import MenuIcon from '@mui/icons-material/Menu';
import CircularProgress from "@mui/material/CircularProgress";
import { EditorContainerBox, FormLoadingButton } from "./styled";
import { useTheme, styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useGetTags } from "hooks/useGetTags";
import { useLessonFormValidator } from "hooks/useLessonFormValidator";
import { convertFileToBase64, convertImageFileToHtmlImage, convertBase64ToFile } from "helpers/fileHelper";
import { useUpdateLessonNodes } from "hooks/useUpdateLessonNodes";
import { IUpdateLessonNodesRequest } from "api/models/request/IUpdateLessonNodesRequest";
import { LessonFormDrawer } from "components/views/editor/components/LessonFormDrawer";
import { NodeEditor } from "components/views/editor/components/NodeEditor";
import { useNodeEditorValidator } from "hooks/useNodeEditorValidator";
import { LessonForm } from "components/views/editor/models/LessonForm";
import { useGetLessonDetails } from "hooks/useGetLessonDetails";
import { useNavigate, useParams } from "react-router-dom";
import { Image } from "components/views/editor/models/Image";
import { UserRoles } from "constants/userRoles";
import AuthGuard from "context/auth/AuthGuard";
import { useEditLesson } from "hooks/useEditLesson";
import { useUpdateLessonImage } from "hooks/useUpdateLessonImage";
import { useSnackbar } from "notistack";
import { useGetTrainingWithProgress } from "hooks/useGetTrainingWithProgress";
import { LessonFormFields } from "constants/lessonFormFields";
import { ConfirmNavigationDialog } from "components/views/editor/components/ConfirmNavigationDialog";
import { useNavigationBlocker } from "hooks/useNavigationBlocker";
import { useAuthContext } from "hooks/useAuthContext";
import { RoutePaths } from "constants/RoutePaths";
import { LoadingBox, NodeEditorContentBox, ActionButtonsBox, StyledSpeedDial, StyledSpeedDialAction } from "./UpdateTrainingPage.styles";

const StyledHiddenInput = styled('input')({
  display: "none"
});

export const UpdateTrainingPage = () => {

    const { id } = useParams();
    const [editor, setEditor] = useState<any>();
    const [lessonFormData, setLessonFormData] = useState<LessonForm>();
    const [openSpeedDial, setOpenSpeedDial] = useState(false);
    const { lessonDetails, fetchLessonDetails, fetchLessonDetailsPending } = useGetLessonDetails(id ?? '');
    const { tags, fetchTags, isLoading: getTagsPending } = useGetTags();
    const { isValid: isLessonFormValid, reset: resetFormErrors, errors: formValidationErrors } = useLessonFormValidator();
    const { isValid: isNodeEditorValid } = useNodeEditorValidator();
    const { editLesson, editingLesson: editLessonPending } = useEditLesson();
    const { updateLessonImage, updateLessonImagePending } = useUpdateLessonImage();
    const { updateLessonNodes, isLoading: updateLessonNodesPending } = useUpdateLessonNodes();
    const { fetchTrainingWithProgress, fetchTrainingWithProgressPending } = useGetTrainingWithProgress();
    const inputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const { onWorkspaceChanged } = useAuthContext();
    const { enqueueSnackbar } = useSnackbar();
    const updateLessonPending = editLessonPending || updateLessonImagePending || updateLessonNodesPending;
    const hasDataChanged = useMemo(() => () => {
        if (!editor) return false;
        const jsonConfig = editor.toCustomJSON().jsonConfig;
        return Object.keys(jsonConfig.nodes).length > 0 && JSON.stringify(lessonDetails?.nodesConfiguration) !== JSON.stringify(jsonConfig)
    }, [editor, lessonDetails?.nodesConfiguration]);
    const { blocker, setBlockerActive } = useNavigationBlocker(hasDataChanged);

    const handleFormDataChange = useCallback((lessonFormField: LessonFormFields, newLessonFormData: LessonForm) => {
        if (Object.keys(formValidationErrors).length > 0) resetFormErrors([lessonFormField]);
        setLessonFormData(newLessonFormData);
    }, [formValidationErrors, resetFormErrors]);

    const onEditorChange = useCallback((editor: object) => {
        setEditor(editor);
    }, []);

    const handleSaveClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!lessonDetails || !lessonFormData || !isLessonFormValid(lessonFormData)) return;
        if (editor && editor.nodes.length > 0 && !isNodeEditorValid(editor)) return;

        const editLessonResponse = await editLesson({
            lessonId: lessonDetails.lessonId,
            lessonName: lessonFormData.name,
            lessonDescription: lessonFormData.description,
            tagA: lessonFormData.tagA,
            tagB: lessonFormData.tagB,
            maxPoints: lessonFormData.maxPoints
        });
        if (!editLessonResponse?.success) return;

        if (lessonFormData.image) {
            const base64String = await convertFileToBase64(lessonFormData.image.file);
            const base64WithoutPrefix = (base64String as string).split(',')[1];
            const extension = lessonFormData.image.file.name.split('.').pop() ?? null;
            if (base64WithoutPrefix && extension && base64WithoutPrefix !== lessonDetails.photoBase64) {
                const updateLessonImageResponse = await updateLessonImage({
                    lessonId: lessonDetails.lessonId,
                    base64Photo: base64WithoutPrefix,
                    photoExtension: extension
                });
                if (!updateLessonImageResponse?.success) return;
            }
        }

        if (editor && editor.nodes.length > 0) {
            const updateLessonNodesRequest: IUpdateLessonNodesRequest = editor.toCustomJSON();
            updateLessonNodesRequest.lessonId = lessonDetails.lessonId;

            const updateLessonNodesResponse = await updateLessonNodes(updateLessonNodesRequest);
            if (!updateLessonNodesResponse?.success) return;
        }

        const fetchTagsResponse = await fetchTags();
        if (!fetchTagsResponse?.success) return;

        const fetchLessonDetailsResponse = await fetchLessonDetails();
        if (!fetchLessonDetailsResponse?.success) return;
    };

    const handleExportToVrClick = async () => {
        if (!lessonDetails || fetchTrainingWithProgressPending) return;
        const response = await fetchTrainingWithProgress({ lessonId: lessonDetails.lessonId });
        if (response?.success && response.data) {
            const fileName = lessonDetails.lessonName + '.json';
            const contentType = 'text/plain';
            let trainingForExportJson = JSON.stringify(response.data, null, 4);
            let a = document.createElement("a");
            const file = new Blob([trainingForExportJson], { type: contentType });
            a.href = URL.createObjectURL(file);
            a.download = fileName;
            a.click();
        }
    }

    const handleExportClick = () => {
        if (!lessonDetails || !lessonFormData || !editor || !isLessonFormValid(lessonFormData) || !isNodeEditorValid(editor)) return;
        const fileName = lessonFormData.name + '.txt';
        const contentType = 'text/plain';
        const trainingForExportTxt = JSON.stringify(editor.toJSON(), null, 4);
        let a = document.createElement("a");
        const file = new Blob([trainingForExportTxt], { type: contentType });
        a.href = URL.createObjectURL(file);
        a.download = fileName;
        a.click();
    }

    const handleImportClick = () => {
        if (inputRef.current) inputRef.current.click();
    }

    const handleImportFileOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const reader = new FileReader();
            reader.onload = function (event) {
                const text = event.target?.result;
                try {
                    // @ts-ignore
                    const data = JSON.parse(text);
                    if (typeof data === 'object' && data !== null) {
                        editor.fromJSON(data);
                    }
                } catch (error) {
                    enqueueSnackbar(t("lessonEditor.errorImportNodeEditorFile"), {
                        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                        variant: 'error'
                    });
                }
            }
            reader.readAsText(file);
        }
    }

    const handleOpenSpeedDial = () => setOpenSpeedDial(true);

    const handleCloseSpeedDial = () => setOpenSpeedDial(false);

    const handleCancelDialog = () => { blocker.reset?.(); }

    const handleConfirmDialog = () => { blocker.proceed?.(); }

    useEffect(() => {
        const setInitialValues = async () => {
            let image: Image | null = null;
            if (lessonDetails && lessonDetails.photoBase64 && lessonDetails.photoExtension) {
                const mimeType = `image/${lessonDetails.photoExtension}`;
                const fileName = `lessonImageTemp.${lessonDetails.photoExtension}`;
                const imageFile = convertBase64ToFile(lessonDetails.photoBase64, fileName, mimeType);
                const htmlImage = await convertImageFileToHtmlImage(imageFile);
                image = { file: imageFile, width: htmlImage.naturalWidth, height: htmlImage.naturalHeight };
            }

            const initialValues: LessonForm = {
                description: lessonDetails ? lessonDetails.description : null,
                image: image,
                name: lessonDetails ? lessonDetails.lessonName : "",
                photoExtension: lessonDetails ? lessonDetails.photoExtension : null,
                tagA: lessonDetails ? lessonDetails.tagA : null,
                tagB: lessonDetails ? lessonDetails.tagB : null,
                maxPoints: lessonDetails ? lessonDetails.maxPoints : 0,
            }

            setLessonFormData(initialValues);
        }

        setInitialValues();

    }, [lessonDetails]);

    useEffect(() => {
        const handleWorkspaceChange = () => {
            setBlockerActive(false);
            navigate(RoutePaths.EDITOR);
        };

        const unsubscribe = onWorkspaceChanged(handleWorkspaceChange);

        return () => {
            unsubscribe();
        }
    }, [navigate, onWorkspaceChanged, setBlockerActive]);

    return (
        <AuthGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron]}>
            <EditorContainerBox>
                {!lessonFormData || !tags || getTagsPending || !lessonDetails || fetchLessonDetailsPending ? (
                    <LoadingBox>
                        <CircularProgress color="primary" />
                    </LoadingBox>
                ) : (
                    <NodeEditorContentBox id="node-editor-content">
                        <LessonFormDrawer
                            tags={tags}
                            lessonFormData={lessonFormData}
                            formValidationErrors={formValidationErrors}
                            onLessonDataChange={handleFormDataChange} />
                        <ActionButtonsBox openSpeedDial={openSpeedDial}>
                            <FormLoadingButton
                                variant="outlined"
                                color="primary"
                                loading={updateLessonPending}
                                onClick={() => navigate(-1)}
                            >
                                {t("lessonEditor.cancel")}
                            </FormLoadingButton>
                            <FormLoadingButton
                                variant="contained"
                                loading={updateLessonPending}
                                onClick={handleSaveClick}
                            >
                                {t("lessonEditor.save")}
                            </FormLoadingButton>
                            <StyledSpeedDial
                                ariaLabel=""
                                FabProps={{ size: "small", sx: { bgcolor: theme.palette.primary.main } }}
                                icon={<MenuIcon />}
                                onClose={handleCloseSpeedDial}
                                onOpen={handleOpenSpeedDial}
                                open={openSpeedDial}
                            >
                                <StyledSpeedDialAction
                                    key={"speed-dial-export-to-vr"}
                                    icon={<VrpanoIcon />}
                                    tooltipTitle={t('lessonEditor.exportToVrApp')}
                                    tooltipOpen
                                    FabProps={{ disabled: fetchTrainingWithProgressPending }}
                                    onClick={handleExportToVrClick}
                                />
                                <StyledSpeedDialAction
                                    key={"speed-dial-export"}
                                    icon={<SaveIcon />}
                                    tooltipTitle={t('lessonEditor.exportFile')}
                                    tooltipOpen
                                    onClick={handleExportClick}
                                />
                                <StyledSpeedDialAction
                                    key={"speed-dial-import"}
                                    icon={<UploadFileIcon />}
                                    tooltipTitle={t('lessonEditor.importFile')}
                                    tooltipOpen
                                    onClick={handleImportClick}
                                />
                            </StyledSpeedDial>
                        </ActionButtonsBox>
                        <NodeEditor
                            lessonId={lessonDetails.lessonId}
                            config={lessonDetails.nodesConfiguration}
                            onEditorChange={onEditorChange}
                        />
                        <StyledHiddenInput
                            ref={inputRef}
                            accept="text/plain"
                            type="file"
                            onChange={handleImportFileOnChange}
                        />
                    </NodeEditorContentBox>
                )}
                <ConfirmNavigationDialog
                    open={blocker.state === "blocked"}
                    handleCancel={handleCancelDialog}
                    handleConfirm={handleConfirmDialog}
                    message={t("nodeEditor.unsavedChanges")}
                />
            </EditorContainerBox>
        </AuthGuard>
    );
}